import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserDocument } from '../services/userService';
import { 
  isAdmin, 
  promoteToAdminByUid, 
  demoteFromAdmin 
} from '../services/userManagement';
import { backfillLastLogin } from '../services/userManagement/migrateLastLogin';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Shield, User, Award, ArrowUp, ArrowDown, RefreshCw } from 'react-feather';

const AdminUsersPage = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserDoc, setCurrentUserDoc] = useState(null);
  const [updating, setUpdating] = useState({});
  const [error, setError] = useState(null);

  // Check if current user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser?.uid) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getUserDocument(currentUser.uid);
        setCurrentUserDoc(userDoc);

        console.log('­ƒöì [AdminUsersPage] Current user doc:', userDoc);
        console.log('­ƒöì [AdminUsersPage] User role:', userDoc?.role);
        console.log('­ƒöì [AdminUsersPage] User email:', userDoc?.email);
        console.log('­ƒöì [AdminUsersPage] Is admin?', isAdmin(userDoc));

        if (!userDoc) {
          setError('User document not found. Please sign out and sign in again.');
          setLoading(false);
          return;
        }

        if (!isAdmin(userDoc)) {
          const userEmail = userDoc.email || currentUser.email;
          const userRole = userDoc.role || 'user';
          
          // Check if this is the admin email but not yet promoted
          if (userEmail && userEmail.toLowerCase() === 'ze_casal@hotmail.com') {
            setError(`Admin email detected but not yet promoted. Please sign out and sign back in to auto-promote. Current role: ${userRole}`);
          } else {
            setError(`Access denied. Admin privileges required. Current role: ${userRole}. Email: ${userEmail}`);
          }
          setLoading(false);
          return;
        }

        // User is admin, load users
        await loadUsers();
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('Failed to verify admin status');
        setLoading(false);
      }
    };

    checkAdmin();
  }, [currentUser]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      
      // Fetch all users - don't use orderBy to avoid missing users without createdAt
      // We'll sort in JavaScript instead
      const querySnapshot = await getDocs(usersRef);
      
      console.log(`­ƒôè [AdminUsersPage] Fetched ${querySnapshot.size} users from Firestore`);

      const usersList = [];
      const targetUid = 'a3XBwlLF9vSIi01DY6zsKNcgscG3';
      let foundTargetUser = false;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Check if this is the missing user
        if (doc.id === targetUid) {
          foundTargetUser = true;
          console.log(`Ô£à [AdminUsersPage] Found target user ${targetUid}:`, data);
        }
        
        // Convert Firestore timestamps to Date objects
        const convertTimestamp = (ts) => {
          if (!ts) return null;
          if (ts && typeof ts.toDate === 'function') {
            return ts.toDate();
          }
          if (ts instanceof Date) {
            return ts;
          }
          if (typeof ts === 'string') {
            return new Date(ts);
          }
          return null;
        };
        
        usersList.push({
          id: doc.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          lastLoginAt: convertTimestamp(data.lastLoginAt),
          updatedAt: convertTimestamp(data.updatedAt),
        });
      });
      
      if (!foundTargetUser) {
        console.warn(`ÔÜá´©Å [AdminUsersPage] Target user ${targetUid} not found in query results`);
        // Try to fetch the specific user document directly
        try {
          const userDocRef = doc(db, 'users', targetUid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const data = userDocSnap.data();
            console.log(`Ô£à [AdminUsersPage] Found target user via direct fetch:`, data);
            const convertTimestamp = (ts) => {
              if (!ts) return null;
              if (ts && typeof ts.toDate === 'function') return ts.toDate();
              if (ts instanceof Date) return ts;
              if (typeof ts === 'string') return new Date(ts);
              return null;
            };
            usersList.push({
              id: targetUid,
              ...data,
              createdAt: convertTimestamp(data.createdAt),
              lastLoginAt: convertTimestamp(data.lastLoginAt),
              updatedAt: convertTimestamp(data.updatedAt),
            });
          } else {
            console.error(`ÔØî [AdminUsersPage] User document ${targetUid} does not exist in Firestore`);
          }
        } catch (err) {
          console.error(`ÔØî [AdminUsersPage] Error fetching target user:`, err);
        }
      }

      // Sort by createdAt if available (fallback for users without createdAt)
      usersList.sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt - a.createdAt;
      });

      setUsers(usersList);
      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteToAdmin = async (uid, email) => {
    if (!window.confirm(`Promote ${email} to admin?`)) {
      return;
    }

    try {
      setUpdating({ ...updating, [uid]: true });
      await promoteToAdminByUid(uid);
      await loadUsers(); // Reload users
    } catch (err) {
      console.error('Error promoting user:', err);
      alert('Failed to promote user to admin');
    } finally {
      setUpdating({ ...updating, [uid]: false });
    }
  };

  const handleDemoteFromAdmin = async (uid, email) => {
    if (!window.confirm(`Demote ${email} from admin?`)) {
      return;
    }

    try {
      setUpdating({ ...updating, [uid]: true });
      await demoteFromAdmin(uid);
      await loadUsers(); // Reload users
    } catch (err) {
      console.error('Error demoting user:', err);
      alert('Failed to demote user from admin');
    } finally {
      setUpdating({ ...updating, [uid]: false });
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    try {
      if (typeof date === 'string') {
        const parsed = new Date(date);
        if (isNaN(parsed.getTime())) return 'Never';
        return parsed.toLocaleString();
      }
      if (date instanceof Date) {
        if (isNaN(date.getTime())) return 'Never';
        return date.toLocaleString();
      }
      return 'Never';
    } catch (error) {
      return 'Never';
    }
  };

  // Get last login date with fallback to updatedAt
  const getLastLoginDate = (user) => {
    if (user.lastLoginAt) {
      return user.lastLoginAt;
    }
    // Fallback to updatedAt if lastLoginAt is missing (for users who haven't logged in since new system)
    if (user.updatedAt) {
      return user.updatedAt;
    }
    return null;
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan?.toLowerCase()) {
      case 'free':
        return 'bg-white text-[#0c0c1a]';
      case 'pro':
        return 'bg-[#24BDFF] text-[#1B0E51]';
      case 'growth':
        return 'bg-[#CE51FF] text-[#1B0E51]';
      default:
        return 'bg-white text-[#0c0c1a]';
    }
  };

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto bg-white px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center justify-center py-16">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin mb-5"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 overflow-y-auto bg-white px-4 sm:px-6 py-4 sm:py-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6" style={{ backgroundColor: '#0c0c1a' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield size={24} className="text-primary-500" />
            <h1 
              className="text-white"
              style={{
                fontFamily: "'Geist', sans-serif",
                fontSize: '24px',
                fontWeight: 600,
                lineHeight: '1.2',
                letterSpacing: '-0.24px'
              }}
            >
              User Management
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  await backfillLastLogin();
                  await loadUsers();
                  alert('Last login dates backfilled successfully!');
                } catch (error) {
                  console.error('Backfill error:', error);
                  alert('Failed to backfill last login dates');
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              style={{
                fontFamily: "'Geist', sans-serif",
                fontSize: '14px',
                fontWeight: 500
              }}
              title="Backfill lastLoginAt for users missing it"
            >
              <RefreshCw size={16} />
              <span>Backfill Login Dates</span>
            </button>
            <button
              onClick={loadUsers}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              style={{
                fontFamily: "'Geist', sans-serif",
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#1b1b2d] rounded-lg p-4">
            <div className="text-white text-opacity-70 text-sm mb-1">Total Users</div>
            <div className="text-white text-2xl font-semibold">{users.length}</div>
          </div>
          <div className="bg-[#1b1b2d] rounded-lg p-4">
            <div className="text-white text-opacity-70 text-sm mb-1">Admins</div>
            <div className="text-white text-2xl font-semibold">
              {users.filter(u => u.role === 'admin').length}
            </div>
          </div>
          <div className="bg-[#1b1b2d] rounded-lg p-4">
            <div className="text-white text-opacity-70 text-sm mb-1">Pro Users</div>
            <div className="text-white text-2xl font-semibold">
              {users.filter(u => u.plan === 'pro').length}
            </div>
          </div>
          <div className="bg-[#1b1b2d] rounded-lg p-4">
            <div className="text-white text-opacity-70 text-sm mb-1">Growth Users</div>
            <div className="text-white text-2xl font-semibold">
              {users.filter(u => u.plan === 'growth').length}
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-[#1b1b2d] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2c2c3f]">
                  <th className="px-6 py-3 text-left text-white text-sm font-semibold" style={{ fontFamily: "'Geist', sans-serif" }}>Email</th>
                  <th className="px-6 py-3 text-left text-white text-sm font-semibold" style={{ fontFamily: "'Geist', sans-serif" }}>Role</th>
                  <th className="px-6 py-3 text-left text-white text-sm font-semibold" style={{ fontFamily: "'Geist', sans-serif" }}>Plan</th>
                  <th className="px-6 py-3 text-left text-white text-sm font-semibold" style={{ fontFamily: "'Geist', sans-serif" }}>Provider</th>
                  <th className="px-6 py-3 text-left text-white text-sm font-semibold" style={{ fontFamily: "'Geist', sans-serif" }}>Last Login</th>
                  <th className="px-6 py-3 text-left text-white text-sm font-semibold" style={{ fontFamily: "'Geist', sans-serif" }}>Created</th>
                  <th className="px-6 py-3 text-left text-white text-sm font-semibold" style={{ fontFamily: "'Geist', sans-serif" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-[#2c2c3f] hover:bg-[#2c2c3f] transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-white" style={{ fontFamily: "'Geist', sans-serif", fontSize: '14px' }}>
                        {user.email}
                      </div>
                      {user.displayName && (
                        <div className="text-white text-opacity-60 text-xs mt-1">{user.displayName}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {user.role === 'admin' ? (
                          <Award size={16} className="text-yellow-400" />
                        ) : (
                          <User size={16} className="text-white text-opacity-60" />
                        )}
                        <span className="text-white capitalize" style={{ fontFamily: "'Geist', sans-serif", fontSize: '14px' }}>
                          {user.role || 'user'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold uppercase ${getPlanBadgeColor(user.plan)}`} style={{ fontFamily: "'Geist Mono', monospace" }}>
                        {user.plan || 'free'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white text-opacity-70" style={{ fontFamily: "'Geist', sans-serif", fontSize: '14px' }}>
                        {user.email || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white text-opacity-70" style={{ fontFamily: "'Geist', sans-serif", fontSize: '14px' }}>
                        {formatDate(getLastLoginDate(user))}
                      </span>
                      {!user.lastLoginAt && user.updatedAt && (
                        <div className="text-white text-opacity-50 text-xs mt-1" title="Estimated from updatedAt - will be accurate after next login">
                          (estimated)
                        </div>
                      )}
                      {!user.lastLoginAt && !user.updatedAt && (
                        <div className="text-white text-opacity-50 text-xs mt-1" title="User needs to log in to set lastLoginAt">
                          (needs login)
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white text-opacity-70" style={{ fontFamily: "'Geist', sans-serif", fontSize: '14px' }}>
                        {formatDate(user.createdAt)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {user.role === 'admin' ? (
                          <button
                            onClick={() => handleDemoteFromAdmin(user.id, user.email)}
                            disabled={updating[user.id] || user.id === currentUser?.uid}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title={user.id === currentUser?.uid ? "Cannot demote yourself" : "Demote from admin"}
                          >
                            <ArrowDown size={14} />
                            <span>Demote</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => handlePromoteToAdmin(user.id, user.email)}
                            disabled={updating[user.id]}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ArrowUp size={14} />
                            <span>Promote</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {users.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white text-opacity-70">No users found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;

